import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaStar,
  FaRegStar,
  FaCheck,
  FaExclamationTriangle,
  FaChartBar,
  FaSearch,
  FaClock,
} from "react-icons/fa";
import AdminLayout from "../../components/AdminLayout";
import api from "../../utils/api";
import { usePopup } from "../../context/PopupContext";
import { useTranslation } from "react-i18next";
import Pagination from "../../components/Pagination";
import "./AdminReviewModeration.css";

const PER_PAGE = 10;

const AdminReviewModeration = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showToast, showConfirm } = usePopup();
  const [, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterRating, setFilterRating] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [page, setPage] = useState(1);

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      try {
        const userObj = JSON.parse(userData);
        setUser(userObj);
        if (userObj.role !== "admin") {
          navigate("/");
        } else {
          fetchReviews();
        }
      } catch (error) {
        console.error("Error parsing user data:", error);
        setLoading(false);
      }
    } else {
      navigate("/login");
    }
  }, [navigate]);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const response = await api.get("/reviews");
      setReviews(response.data || []);
    } catch (error) {
      console.error("Error fetching reviews:", error);
      setReviews([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (reviewId) => {
    try {
      await api.put(`/admin/reviews/${reviewId}/approve`);
      fetchReviews();
      showToast(t("adminReviewModeration.approveSuccess"), "success");
    } catch (error) {
      console.error("Error approving review:", error);
      showToast(t("adminReviewModeration.approveFailed"), "error");
    }
  };

  const handleToggleVisibility = (review) => {
    const isHidden = review.isVisible === false;
    showConfirm({
      title: isHidden
        ? t("adminReviewModeration.unhideTitle")
        : t("adminReviewModeration.hideTitle"),
      message: isHidden
        ? t("adminReviewModeration.unhideMessage")
        : t("adminReviewModeration.hideMessage"),
      confirmLabel: isHidden
        ? t("adminReviewModeration.unhide")
        : t("adminReviewModeration.hide"),
      cancelLabel: t("common.cancel"),
      onConfirm: async () => {
        try {
          if (isHidden) {
            await api.put(`/admin/reviews/${review._id}/unhide`);
            showToast(t("adminReviewModeration.unhideSuccess"), "success");
          } else {
            await api.put(`/admin/reviews/${review._id}/hide`);
            showToast(t("adminReviewModeration.hideSuccess"), "success");
          }
          fetchReviews();
        } catch (error) {
          console.error("Error toggling review visibility:", error);
          showToast(
            isHidden
              ? t("adminReviewModeration.unhideFailed")
              : t("adminReviewModeration.hideFailed"),
            "error",
          );
        }
      },
    });
  };

  const handleDelete = async (reviewId) => {
    showConfirm({
      title: t("adminReviewModeration.deleteTitle"),
      message: t("adminReviewModeration.deleteMessage"),
      confirmLabel: t("adminReviewModeration.delete"),
      cancelLabel: t("common.cancel"),
      onConfirm: async () => {
        try {
          await api.delete(`/admin/reviews/${reviewId}`);
          fetchReviews();
          showToast(t("adminReviewModeration.deleteSuccess"), "success");
        } catch (error) {
          console.error("Error deleting review:", error);
          showToast(t("adminReviewModeration.deleteFailed"), "error");
        }
      },
    });
  };

  const filteredReviews = reviews.filter((review) => {
    const matchesRating =
      filterRating === "all" || review.rating === parseInt(filterRating);
    const matchesStatus =
      filterStatus === "all" ||
      (filterStatus === "verified" && review.isVerified) ||
      (filterStatus === "unverified" && !review.isVerified) ||
      (filterStatus === "hidden" && review.isVisible === false);
    const matchesSearch =
      review.product?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      review.consumer?.name
        ?.toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      review.comment?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesRating && matchesStatus && matchesSearch;
  });

  const totalPages = Math.ceil(filteredReviews.length / PER_PAGE);
  const pageReviews = filteredReviews.slice(
    (page - 1) * PER_PAGE,
    page * PER_PAGE,
  );

  useEffect(() => {
    setPage(1);
  }, [searchQuery, filterRating, filterStatus]);

  const stats = {
    total: reviews.length,
    verified: reviews.filter((r) => r.isVerified).length,
    unverified: reviews.filter((r) => !r.isVerified).length,
    averageRating:
      reviews.length > 0
        ? (
            reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length
          ).toFixed(1)
        : 0,
  };

  const renderStars = (rating) => {
    return (
      <span className="admin-review-moderation-stars-inline">
        {[1, 2, 3, 4, 5].map((i) =>
          i <= rating ? (
            <FaStar key={i} aria-hidden />
          ) : (
            <FaRegStar key={i} aria-hidden />
          ),
        )}
      </span>
    );
  };

  return (
    <AdminLayout>
      <div className="admin-review-moderation-content">
        {/* Page Header */}
        <div className="admin-review-moderation-page-header">
          <div>
            <h1 className="admin-review-moderation-page-title">
              {t("adminReviewModeration.title")}
            </h1>
            <p className="admin-review-moderation-page-subtitle">
              {t("adminReviewModeration.subtitle")}
            </p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="admin-review-moderation-stats-cards-grid">
          <div className="admin-review-moderation-stat-card">
            <div className="admin-review-moderation-stat-icon">
              <FaStar aria-hidden />
            </div>
            <div className="admin-review-moderation-stat-content">
              <div className="admin-review-moderation-stat-value">
                {stats.total}
              </div>
              <div className="admin-review-moderation-stat-label">
                {t("adminReviewModeration.totalReviews")}
              </div>
            </div>
          </div>
          <div className="admin-review-moderation-stat-card">
            <div className="admin-review-moderation-stat-icon">
              <FaCheck aria-hidden />
            </div>
            <div className="admin-review-moderation-stat-content">
              <div className="admin-review-moderation-stat-value">
                {stats.verified}
              </div>
              <div className="admin-review-moderation-stat-label">
                {t("adminReviewModeration.verifiedReviews")}
              </div>
            </div>
          </div>
          <div className="admin-review-moderation-stat-card warning">
            <div className="admin-review-moderation-stat-icon">
              <FaExclamationTriangle aria-hidden />
            </div>
            <div className="admin-review-moderation-stat-content">
              <div className="admin-review-moderation-stat-value">
                {stats.unverified}
              </div>
              <div className="admin-review-moderation-stat-label">
                {t("adminReviewModeration.unverifiedReviews")}
              </div>
            </div>
          </div>
          <div className="admin-review-moderation-stat-card highlight">
            <div className="admin-review-moderation-stat-icon">
              <FaChartBar aria-hidden />
            </div>
            <div className="admin-review-moderation-stat-content">
              <div className="admin-review-moderation-stat-value">
                {stats.averageRating}
              </div>
              <div className="admin-review-moderation-stat-label">
                {t("adminReviewModeration.averageRating")}
              </div>
            </div>
          </div>
        </div>

        {/* Filters Section */}
        <div className="admin-review-moderation-filters-card">
          <div className="admin-review-moderation-search-box">
            <input
              type="text"
              placeholder={t("adminReviewModeration.searchPlaceholder")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="admin-review-moderation-search-input"
            />
            <FaSearch
              className="admin-review-moderation-search-icon"
              aria-hidden
            />
          </div>
          <div className="admin-review-moderation-filter-group">
            <select
              value={filterRating}
              onChange={(e) => setFilterRating(e.target.value)}
              className="admin-review-moderation-filter-select"
            >
              <option value="all">
                {t("adminReviewModeration.allRatings")}
              </option>
              <option value="5">5 Stars</option>
              <option value="4">4 Stars</option>
              <option value="3">3 Stars</option>
              <option value="2">2 Stars</option>
              <option value="1">1 Star</option>
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="admin-review-moderation-filter-select"
            >
              <option value="all">
                {t("adminReviewModeration.allStatus")}
              </option>
              <option value="verified">Verified</option>
              <option value="unverified">Unverified</option>
              <option value="hidden">
                {t("adminReviewModeration.filterHidden")}
              </option>
            </select>
          </div>
        </div>

        {/* Reviews Table */}
        <div className="admin-review-moderation-table-card">
          {loading ? (
            <div className="admin-review-moderation-loading-state">
              <div className="admin-review-moderation-spinner"></div>
              <p>{t("adminReviewModeration.loading")}</p>
            </div>
          ) : filteredReviews.length === 0 ? (
            <div className="admin-review-moderation-empty-state">
              <div className="admin-review-moderation-empty-icon">
                <FaStar aria-hidden />
              </div>
              <h3>{t("adminReviewModeration.noReviews")}</h3>
              <p>{t("adminReviewModeration.tryAdjusting")}</p>
            </div>
          ) : (
            <div className="admin-review-moderation-table-container">
              <table className="admin-review-moderation-data-table">
                <thead>
                  <tr>
                    <th>{t("adminReviewModeration.product")}</th>
                    <th>{t("adminReviewModeration.consumer")}</th>
                    <th>{t("adminReviewModeration.rating")}</th>
                    <th>{t("adminReviewModeration.comment")}</th>
                    <th>{t("adminReviewModeration.status")}</th>
                    <th>{t("adminReviewModeration.date")}</th>
                    <th>{t("adminReviewModeration.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {pageReviews.map((review) => (
                    <tr key={review._id}>
                      <td>
                        <div className="admin-review-moderation-product-info">
                          <div className="admin-review-moderation-product-name">
                            {review.product?.name || "N/A"}
                          </div>
                          <div className="admin-review-moderation-product-farmer">
                            by {review.farmer?.name || "Unknown"}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="admin-review-moderation-consumer-info">
                          <div className="admin-review-moderation-consumer-name">
                            {review.consumer?.name || "N/A"}
                          </div>
                          <div className="admin-review-moderation-consumer-email">
                            {review.consumer?.email || ""}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="admin-review-moderation-rating-display">
                          <span className="admin-review-moderation-rating-stars">
                            {renderStars(review.rating)}
                          </span>
                          <span className="admin-review-moderation-rating-number">
                            ({review.rating}/5)
                          </span>
                        </div>
                      </td>
                      <td className="admin-review-moderation-comment-cell">
                        <div className="admin-review-moderation-comment-text">
                          {review.comment || (
                            <em>{t("adminReviewModeration.noComment")}</em>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="admin-review-moderation-status-badges">
                          <span
                            className={`admin-review-moderation-status-badge ${review.isVerified ? "verified" : "unverified"}`}
                          >
                            {review.isVerified ? (
                              <>
                                <FaCheck aria-hidden /> Verified
                              </>
                            ) : (
                              <>
                                <FaClock aria-hidden /> Unverified
                              </>
                            )}
                          </span>
                          {review.isVisible === false && (
                            <span className="admin-review-moderation-status-badge hidden">
                              {t("adminReviewModeration.statusHidden")}
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        {new Date(review.createdAt).toLocaleDateString(
                          "en-US",
                          {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          },
                        )}
                      </td>
                      <td className="admin-review-moderation-actions-cell">
                        <div className="admin-review-moderation-action-buttons">
                          {!review.isVerified && (
                            <button
                              onClick={() => handleApprove(review._id)}
                              className="admin-review-moderation-action-btn approve"
                            >
                              {t("adminReviewModeration.approve")}
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => handleToggleVisibility(review)}
                            className={`admin-review-moderation-action-btn ${review.isVisible === false ? "unhide" : "hide"}`}
                          >
                            {review.isVisible === false
                              ? t("adminReviewModeration.unhide")
                              : t("adminReviewModeration.hide")}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(review._id)}
                            className="admin-review-moderation-action-btn delete"
                          >
                            {t("adminReviewModeration.delete")}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pagination
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminReviewModeration;
